import { motion } from "framer-motion";
import { Bell, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const statusStyles = {
  "Concept": "text-white/60 border-white/20",
  "Alpha": "text-accent border-accent/50",
  "Pre-Order Soon": "text-black bg-accent border-accent",
};

export default function Releases({ data }) {
  const notify = (e, title) => {
    e.preventDefault();
    e.stopPropagation();
    toast.success(`You'll be notified about ${title}`, {
      description: "Priority updates enabled.",
    });
  };

  return (
    <section
      id="releases"
      data-testid="releases-section"
      className="relative py-24 md:py-40 px-6 md:px-12 lg:px-16"
    >
      <div className="max-w-[1600px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14 md:mb-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="w-12 h-px bg-accent" />
              <span className="font-body text-[11px] uppercase tracking-[0.35em] text-accent">
                Roadmap dei progetti
              </span>
            </div>
            <h2
              data-testid="releases-title"
              className="font-heading uppercase text-white text-5xl md:text-7xl lg:text-8xl leading-[0.9]"
            >
              Prossime uscite
            </h2>
          </motion.div>
          <div className="font-body text-sm text-white/50 max-w-xs">
            Ogni progetto qui sotto è sviluppato interamente in-house presso Ashvolt Studios.
          </div>
        </div>

        {/* Editorial list layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
          {data.map((item, i) => (
            <motion.article
              key={item.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, delay: (i % 2) * 0.1, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`release-card-${item.id}`}
              className={`group relative border border-white/10 bg-[#09090B] overflow-hidden hover:border-white/30 transition-colors duration-500 ${
                i % 3 === 0 ? "md:col-span-2" : ""
              }`}
            >
              <Link to={`/games/${item.id}`} className="block">
              <div className={`relative overflow-hidden ${i % 3 === 0 ? "aspect-[21/9]" : "aspect-[4/3]"}`}>
                <img
                  src={item.cover}
                  alt={item.title}
                  className="w-full h-full object-cover transition-transform duration-[1.8s] ease-out group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="absolute top-5 left-5 right-5 flex items-start justify-between">
                  <span className="font-body text-[10px] uppercase tracking-[0.3em] text-white/70 backdrop-blur-sm bg-black/30 border border-white/10 px-3 py-1.5">
                    {item.codename}
                  </span>
                  <span
                    className={`font-body text-[10px] uppercase tracking-[0.3em] px-3 py-1.5 border backdrop-blur-sm ${statusStyles[item.status] || "text-white/60 border-white/20"}`}
                  >
                    {item.status}
                  </span>
                </div>
              </div>

              <div className="p-6 md:p-8 flex flex-col gap-5">
                <div className="flex items-start justify-between gap-4">
                  <h3 className="font-heading text-3xl md:text-4xl uppercase text-white leading-none">
                    {item.title}
                  </h3>
                  <ArrowUpRight
                    size={28}
                    className="text-white/40 -translate-y-1 transition-all duration-500 group-hover:text-accent group-hover:translate-x-1 group-hover:-translate-y-2"
                  />
                </div>
                <p className="font-body text-sm md:text-base text-white/55 leading-relaxed max-w-lg">
                  {item.summary}
                </p>
                <div className="flex items-center justify-between border-t border-white/10 pt-5 mt-2">
                  <div className="flex items-center gap-4">
                    <span className="font-body text-[10px] uppercase tracking-[0.3em] text-white/50">
                      {item.window}
                    </span>
                    <span className="w-1 h-1 rounded-full bg-white/20" />
                    <div className="flex items-center gap-2">
                      {item.platforms.map((p) => (
                        <span
                          key={p}
                          className="font-body text-[10px] uppercase tracking-[0.25em] text-white/70 border border-white/15 px-2 py-1"
                        >
                          {p}
                        </span>
                      ))}
                    </div>
                  </div>
                  <button
                    data-testid={`alert-btn-${item.id}`}
                    onClick={(e) => notify(e, item.title)}
                    className="group/btn inline-flex items-center gap-2 text-white/70 hover:text-accent transition-colors"
                  >
                    <Bell size={13} />
                    <span className="font-body text-[10px] uppercase tracking-[0.3em]">
                      Avvisami
                    </span>
                  </button>
                </div>
              </div>
              </Link>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
